/**
 * Posts journey milestones OUT to the page hosting the widget.
 *
 * Outbound only. No `message` listener is added anywhere in this feature —
 * accepting messages is where the real risk lives, and we never do.
 *
 * Not to be confused with the internal behavioural tracking, which sends
 * camelCase events to our own API. Both fire at some of the same moments;
 * they are different transports to different audiences, and the tracking
 * taxonomy is free to change while this one is a contract with partner sites.
 */
import { store } from '@/context/store';
import { getEmbedContext } from '@/utils/getEmbedContext';

import {
    EJourneyEvent,
    IJourneyEventMessage,
    JOURNEY_EVENT_SOURCE,
    JOURNEY_EVENT_VERSION,
} from './EJourneyEvent';

/**
 * TOOL_ENGAGED means the FIRST genuine interaction, so it is sent once per
 * session however many times the engagement call site fires. The other four
 * are allowed to repeat — a customer who goes back and changes an answer can
 * legitimately reach a milestone twice, and the guide tells partners to dedupe.
 */
let toolEngagedSent = false;

/** Test seam: the module-level once-guard outlives a single test. */
export function resetJourneyEventsForTests(): void {
    toolEngagedSent = false;
}

export function emitJourneyEvent(event: EJourneyEvent): void {
    try {
        if (event === EJourneyEvent.TOOL_ENGAGED) {
            if (toolEngagedSent) return;
            toolEngagedSent = true;
        }

        // Opt-in per brand, set in vroom admin. `=== true`, not truthy: an
        // unset flag is omitted from the partner payload rather than sent as
        // false, so absent has to mean off. Every brand is absent today.
        if (store.getState().displayReducer.partner.journeyEventsEnabled !== true) return;

        const message: IJourneyEventMessage = {
            source: JOURNEY_EVENT_SOURCE,
            version: JOURNEY_EVENT_VERSION,
            event,
        };

        const { embedMode, ancestorOrigin, parentUrl } = getEmbedContext();

        if (embedMode === 'snippet') {
            // The loader rendered us into the partner's own DOM, so there is
            // no origin boundary and window IS their page. postMessage would
            // be talking to itself. No origin check is possible here, and the
            // guide tells partners not to write one.
            window.dispatchEvent(new CustomEvent(JOURNEY_EVENT_SOURCE, { detail: message }));
            return;
        }

        if (embedMode !== 'iframe') {
            // 'direct' — the widget is the top-level document on one of our
            // own hosts (SMS link, QR code, a partner linking out). There is
            // no host page to tell.
            return;
        }

        const targetOrigin = resolveParentOrigin(ancestorOrigin, parentUrl);

        /*
         * NEVER fall back to targetOrigin '*'. A wildcard delivers to every
         * frame in the ancestor chain rather than just the embedder, so a
         * widget nested two frames deep would leak a customer's progress to a
         * page that is not the partner's.
         *
         * Both sources below are browser-provided. An embedder can suppress
         * them — a strict referrer policy, a sandbox — but cannot forge them
         * to somebody else's origin, so "resolved, or send nothing" is sound
         * and is not over-cautious.
         *
         * This is the line a future refactor will try to simplify. Do not.
         */
        if (!targetOrigin) return;

        window.parent.postMessage(message, targetOrigin);
    } catch {
        /* Tracking the partner's page must never break the journey. */
    }
}

/**
 * The embedder's origin, or null.
 *
 * `ancestorOrigin` is location.ancestorOrigins resolved to the TOP-LEVEL page
 * — the right answer when a site builder wraps the embed in its own frame.
 * `parentUrl` is document.referrer, origin-only under every browser's default
 * referrer policy. An entry serialises as the string "null" when it has been
 * redacted, which is not an origin.
 */
function resolveParentOrigin(ancestorOrigin: string | null, parentUrl: string | null): string | null {
    for (const candidate of [ancestorOrigin, parentUrl]) {
        if (!candidate || candidate === 'null') continue;
        try {
            const { origin } = new URL(candidate);
            if (origin && origin !== 'null') return origin;
        } catch {
            continue;
        }
    }
    return null;
}
